import ArtistImg from "@src/app/assets/images/artist.svg";
import CandiceChanImg from "@src/app/assets/images/editors/candice-chan.svg";
import CharleneChowImg from "@src/app/assets/images/editors/charlene-chow.svg";
import ChowYianPingImg from "@src/app/assets/images/editors/chow-yian-ping.svg";
import GraceChongImg from "@src/app/assets/images/editors/grace-chong.svg";
import JamesLooImg from "@src/app/assets/images/editors/james-loo.svg";
import JinaYiImg from "@src/app/assets/images/editors/jina.svg";
import JosephineHongImg from "@src/app/assets/images/editors/josephine-hong.svg";
import LingmingLuImg from "@src/app/assets/images/editors/lingming-lu.svg";
import LohWoonYenImg from "@src/app/assets/images/editors/loh-woon-yen.svg";
import ArticleAnnotation from "@src/app/components/Article/body/components/ArticleAnnotation";
import EditorCard from "@src/app/components/EditorCard/EditorCard";
import Space from "@src/app/components/Space";
import StaticSection from "@src/app/components/StaticSection";
import type { BFFAuthorDetail } from "@src/app/types/bff";
import { createMetaTags } from "@src/app/utils/createMetaTags";
import clsx from "clsx";

export const meta = () =>
  createMetaTags({
    title: "About Us | Big Reads, Opinions & Columns on China | ThinkChina",
    description:
      "ThinkChina is an English-language e-magazine under Lianhe Zaobao. We publish original reporting and opinion pieces across many topics, with a China focus.",
    canonical: `https://www.thinkchina.sg/about-us`,
    ogInfo: {
      ogTitle: "About Us | Big Reads, Opinions & Columns on China",
    },
  });

function AboutUsParagraphs({
  content,
  children,
  className,
}: {
  content?: string;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <p className={clsx("text-body-2 text-primary", className)}>
      {children ? (
        children
      ) : content ? (
        <span dangerouslySetInnerHTML={{ __html: content }} />
      ) : null}
    </p>
  );
}

function Paragraphs({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-3">{children}</div>;
}

type AboutUsAuthor = Omit<BFFAuthorDetail, "urlPath">;

function Editor({ author }: { author: AboutUsAuthor }) {
  return (
    <div className="flex flex-col gap-3 py-4">
      <EditorCard author={author as BFFAuthorDetail} />

      {author.bio?.length ? (
        <ArticleAnnotation
          value={author.bio[0]?.value}
          annotations={author.bio[0]?.annotations}
          linkUnderline
        />
      ) : (
        <span>{author.bio_raw}</span>
      )}
    </div>
  );
}

const linkClassName =
  "text-link underline text-link-default hover:text-link-hover";

const editors: AboutUsAuthor[] = [
  {
    id: "loh-woon-yen",
    name: "Loh Woon Yen",
    imageUrl: LohWoonYenImg,
    designation: "Deputy Editor-in-Chief, Chinese Media Group, SPH Media",
    bio_raw:
      "Woon Yen is the Deputy Editor-in-Chief of the Chinese Media Group at SPH Media (CMG), overseeing ThinkChina, product & technology, commercial functions and newsroom operations. Previously, she held roles as Managing Editor, Deputy Digital Editor of CMG and the Business Editor of Lianhe Zaobao. Woon Yen holds a degree in Accounting and Finance from Lancaster University and is a Chartered Financial Analyst.",
  },
  {
    id: "chow-yian-ping",
    name: "Chow Yian Ping",
    imageUrl: ChowYianPingImg,
    designation: "Editor",
    bio_raw: `Yian Ping is the founding editor of ThinkChina. She was a senior correspondent at Lianhe Zaobao (LHZB) where she wrote big reads and features. She has worked as a journalist, a museum curator and a TV producer while maintaining a <a target='_blank' href='https://www.zaobao.com.sg/byline/zhou-yan-bing' class='${linkClassName}'> regular column</a> in LHZB for over 20 years. She has lived in various cities, including Beijing, Hong Kong, Manila, Wellington and Singapore. She also oversaw LHZB's <a target='_blank' href='https://interactive.zaobao.com/zaobao-centennial/oral-history/index.html' class='${linkClassName}'>oral history</a> project celebrating the paper's centennial.`,
    bio: [
      {
        type: "paragraph",
        value: `Yian Ping is the founding editor of ThinkChina. She was a senior correspondent at Lianhe Zaobao (LHZB) where she wrote big reads and features. She has worked as a journalist, a museum curator and a TV producer while maintaining a regular column in LHZB for over 20 years. She has lived in various cities, including Beijing, Hong Kong, Manila, Wellington and Singapore. She also oversaw LHZB's oral history project celebrating the paper's centennial.`,
        annotations: [
          {
            index: 230,
            length: 14,
            name: "external_link",
            url: "https://www.zaobao.com.sg/byline/zhou-yan-bing",
            newWindow: true,
          },
          {
            index: 393,
            length: 12,
            name: "external_link",
            url: "https://interactive.zaobao.com/zaobao-centennial/oral-history/index.html",
            newWindow: true,
          },
        ],
      },
    ],
  },
  {
    id: "charlene-chow",
    name: "Charlene Chow",
    imageUrl: CharleneChowImg,
    designation: "Assistant Editor",
    bio_raw: `Charlene Chow is the Assistant Editor at ThinkChina. She was previously with the Singapore Foreign Service where she worked at various desks and was posted abroad. She later joined a food magazine where she produced features, personality profiles and other print and online stories. She enjoys exploring the worlds of people, culture and food.`,
  },
  {
    id: "candice-chan",
    name: "Candice Chan",
    imageUrl: CandiceChanImg,
    designation: "Executive Translator/Content Producer",
    bio_raw: `Candice was previously a publishing editor and civil servant. She loves words and languages, which prompted her to learn French in NUS and study translation in NTU. She also enjoys books, music, movies, and photographs, all of which tell stories in their own way.`,
  },
  {
    id: "grace-chong",
    name: "Grace Chong",
    imageUrl: GraceChongImg,
    designation: "Translator/Content Producer",
    bio_raw: `Grace pursued her Master's degree in English linguistics right after she completed her Bachelor's degree in Chinese linguistics. She is intrigued by how linguistic theories can be applied across different languages, and if they can't, they'll make good research papers. When she's not translating, she can be found singing, eating, and shopping.`,
  },
  {
    id: "james-loo",
    name: "James Loo",
    imageUrl: JamesLooImg,
    designation: "Translator/Content Producer",
    bio_raw: `James was previously a translator and lecturer at a university in Taiwan and had also worked as a translator for a think tank in Taiwan. He holds a Master's in Translation and Interpretation from National Taiwan Normal University and a Bachelor's degree in English Literature from Nanyang Technological University in Singapore. He continues to be fascinated by how translation can be a metaphor for metaphor, and how metaphor can be a metaphor for translation.`,
  },
  {
    id: "josephine-hong",
    name: "Josephine Hong",
    imageUrl: JosephineHongImg,
    designation: "Copy Editor",
    bio_raw: `Josephine completed her undergraduate degree in Chinese Language and Literature from Fudan University, Shanghai, and is an accredited editor with the Institute of Professional Editors (Australia and New Zealand). She has edited for a trade publisher, government organisation and finance and economics research houses. She is passionate about the art of storytelling in both digital and print mediums.`,
  },
  {
    id: "lu-lingming",
    name: "Lu Lingming",
    imageUrl: LingmingLuImg,
    designation: "Video Journalist",
    bio_raw: `Lu Lingming earned a Master’s degree in Directing from the School of Visual Arts New York. A passionate storyteller with a sharp eye for visual creativity, he has directed independent and commercial projects, winning Best Original Story at the London Short Film Festival and Best Student Director at the Independent Shorts Awards. Outside of filmmaking, Lingming finds inspiration by attending music festivals and discovering hidden spots around the world.`,
  },
  {
    id: "yi-jina",
    name: "Yi Jina",
    imageUrl: JinaYiImg,
    designation: "Video Journalist",
    bio_raw: `Jina graduated from the Wee Kim Wee School of Communication and Information, where she refined her skills in storytelling through videos. She is passionate about leveraging social media and video content to engage and inform the younger demographic. Beyond video production, Jina finds joy in expressing herself through dance and eating spicy food.`,
  },
];

const ABOUT_US_CONTENT = [
  "ThinkChina is an English-language e-magazine focused on China under SPH Media Limited's flagship Chinese daily, Lianhe Zaobao*. We publish original reporting, opinion pieces and columns across a wide spectrum of topics, covering political, economic, socio-cultural and technological developments in China and the Greater China region.",
  "Our extensive network of Chinese-language journalists and writers are mainly from Singapore, China and other parts of Asia. We seek to bring their nuanced and insightful perspectives to the English-speaking world through thoughtful curation and meticulous translation. This myriad of voices is projected from the unique vantage point of multi-ethnic Singapore — a proverbial little red dot in Southeast Asia.",
  `ThinkChina won Gold for Best News Website or Mobile Service at the 2020 World Association of News Publishers (WAN-IFRA) Asian Digital Media Awards. At the 2024 Eppy Awards, it placed third for <a href="https://www.thinkchina.sg/" class="${linkClassName}">Best Magazine Website</a>, joint first place for <a href="https://www.thinkchina.sg/cartoon/comic-other-side-coral-reef" class="${linkClassName}">Best Editorial/Political Cartoon</a> and second place for <a href="https://www.thinkchina.sg/tags/vox-pop" class="${linkClassName}">Best News or Event Feature Video</a> in the "fewer than 1 million unique visitors" category.`,
  "<span class='text-brand-primary'>*</span>Lianhe Zaobao is a Chinese-language daily published by SPH Media Limited. It is one of the few foreign-owned Chinese language media accessible online in China. It has a monthly unique visitorship of 5 million, and a monthly pageview count of 100 million in China.",
];

const CHINESE_NAME_CONTENT = [
  `ThinkChina's Chinese logo is a traditional seal carving with a modern twist. While most seals are carved on stones, our logo is carved on a ceramic sculpture created by Singaporean artist Oh Chai Hoo.`,
  `The carved logo is a fusion of three different scripts: Han dynasty brick engraving (砖文), Song typeface (宋体), and simplified Chinese characters.`,
  `Chai Hoo gave much thought to the design. “思想” (think) has its Chinese character “思” slightly tilted, cleverly mimicking a person's tilted head while thinking. “想” is made up of three parts, the part “目” (eye) is carved horizontally to resemble an eye; the idea is for one to stand beside a tree (木), to see with our eyes (目) and to ponder with our heart (心). A more structured look is adopted for “中国” (China). The word “中” (middle) has retained this balanced form for more than 3500 years, while “国” (country) rendered in simplified Chinese character, gives a stable and firm base to the design.`,
  `There is a “pixelated” border framing the words “思想中国” (ThinkChina), but “思想” (think) ventures beyond the borders, encouraging us to think outside the box. Lastly, the ceramic sculpture mimics a contemporary building, strongly rooted while looking forward and upward, an embodiment of the artist's hopes for ThinkChina.`,
];

const PITCHES_AND_SUBMISSIONS_CONTENT = [
  {
    title: "Commentaries and opinion pieces:",
    items: [
      "(i) A paragraph or two stating the article title and what you would be writing about. It would be good to explain the relevance of the proposed article to current events, reasons why this topic should be further delved into, and why your subject expertise or vantage point could provide some interesting insights on the issue.",
      "(ii) If submitting a draft piece that you would like us to take a look at, please attach a word document including hyperlinks in the text to relevant sources. The word length would usually be around 1,000 words.",
    ],
  },
  {
    title: "Feature stories:",
    items: [
      "(iii) In addition to (i), for feature story pitches, please include an outline of the piece, the interviewees you have in mind and image/video possibilities.",
    ],
  },
  {
    title: "Cartoons and comics:",
    items: ["(iv) Please send fully drawn cartoons."],
  },
];
export default function AboutUsPage() {
  return (
    <div className="container mx-auto max-w-[1020px]">
      <StaticSection title="About Us" className="py-4">
        <Paragraphs>
          {ABOUT_US_CONTENT.map((text, i) => (
            <AboutUsParagraphs key={i} content={text} />
          ))}
        </Paragraphs>
      </StaticSection>

      <StaticSection title="Our Team" className="py-4">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
          {editors.map((editor) => (
            <Editor key={editor.id} author={editor} />
          ))}
        </div>
      </StaticSection>

      <StaticSection title="Pitches and Submissions" className="py-4">
        <Paragraphs>
          <AboutUsParagraphs content="We are open to pitches from analysts and commentators offering global perspectives on China. We are also keen to hear from freelance feature writers, photographers, comic artists or video content producers based in China. " />
          <AboutUsParagraphs>
            If you have an idea or story proposal you would like to share with
            us, please send us an{" "}
            <span>
              <a href="mailto:thinkchina@sph.com.sg" className={linkClassName}>
                email (thinkchina@sph.com.sg)
              </a>
            </span>{" "}
            with the following elements:
          </AboutUsParagraphs>
          {PITCHES_AND_SUBMISSIONS_CONTENT.map((group, i) => (
            <div key={i} className="flex flex-col">
              <p className="text-primary text-body-1 font-bold">
                {group.title}
              </p>
              {group.items.map((item, j) => (
                <AboutUsParagraphs key={j} content={item} />
              ))}
            </div>
          ))}
          <AboutUsParagraphs content="For all pitches and submissions, please provide links to previously published pieces if available; your name, email address and contact number for correspondence; and a short author bio stating your current designation and affiliation. Pieces proposed or submissions should not have been published elsewhere. We do accept submissions in Chinese, but the honorarium would be adjusted accordingly in view of the translation work that would need to be done before publication. As we receive a high volume of emails at times, if you do not hear from us within two weeks, please do send your proposals on to other publications." />
        </Paragraphs>
      </StaticSection>

      <StaticSection title="Our Chinese Name (思想中国)" className="py-4">
        <Paragraphs>
          {CHINESE_NAME_CONTENT.map((text, i) => (
            <AboutUsParagraphs key={i} className="text-body-1">
              {text}
            </AboutUsParagraphs>
          ))}
        </Paragraphs>
      </StaticSection>

      <StaticSection title="About the artist" className="py-4">
        <div className="flex flex-col-reverse gap-6 lg:flex-row lg:gap-8">
          <AboutUsParagraphs className="text-body-1">
            {`Mr Oh Chai Hoo graduated from Nanyang Academy of Fine Arts with a specialisation in Western painting. When he was in Secondary One, his art teacher encouraged him to take up art classes at a community club. At 14, he started painting by the Singapore River, where experienced artists would look over his shoulder at his paintings and give him impromptu masterclasses. He believes that everyone is born with a mission, and his is to be an artist. His art philosophy is to discover emotions and memories (even painful ones), and to translate these into his creations. He has never forgotten why he got started on art, and will continue to keep at it.`}
          </AboutUsParagraphs>

          <img src={ArtistImg} alt="Artist Oh Chai Hoo" className="w-full" />
        </div>
      </StaticSection>
      <Space />
    </div>
  );
}
